// SingleBlog.jsx — public single blog page (SEO-ready)

import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "./SingleBlog.css";


/* API FIXED — was localhost, now matches Blog.jsx / BlogManager.jsx */
const API = "https://www.thehexaplay.com/api/blogs";
const SITE_NAME = "Sezhi Billing";

export default function SingleBlog() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  /* Always open (and switch between) articles at the top */
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [slug]);

  /* ============================================================
     FETCH SINGLE BLOG BY SLUG
  ============================================================ */

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await axios.get(`${API}/${slug}`);

        if (response.data?.blog) {
          setBlog(response.data.blog);
        } else {
          setError("Blog not found");
        }
      } catch (err) {
        console.error("Failed to fetch blog:", err);

        if (err.response?.status === 404) {
          setError("Blog not found");
        } else {
          setError(
            err.response?.data?.message ||
              err.message ||
              "Failed to load blog"
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug]);

  /* ============================================================
     SEO — PAGE TITLE + META DESCRIPTION
     Uses the SEO fields saved from Blog Manager.
  ============================================================ */

  useEffect(() => {
    if (!blog) return;

    const previousTitle = document.title;
    const metaDescription = document.querySelector(
      'meta[name="description"]'
    );
    const previousDescription = metaDescription
      ? metaDescription.getAttribute("content")
      : null;

    const seoTitle = blog.metaTitle || blog.title || "Blog";
    document.title = `${seoTitle} | ${SITE_NAME}`;

    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        blog.metaDescription || blog.excerpt || ""
      );
    }

    return () => {
      document.title = previousTitle;

      if (metaDescription && previousDescription !== null) {
        metaDescription.setAttribute("content", previousDescription);
      }
    };
  }, [blog]);

  /* ============================================================
     FETCH RELATED BLOGS
  ============================================================ */

  useEffect(() => {
    if (!blog?.category) return;

    const fetchRelated = async () => {
      try {
        const response = await axios.get(`${API}/published`);

        const allPublished = response.data?.blogs || [];

        const related = allPublished
          .filter(
            (b) =>
              b.slug !== blog.slug &&
              (b.category === blog.category ||
                (b.tags || []).some((t) =>
                  (blog.tags || []).includes(t)
                ))
          )
          .slice(0, 3);

        setRelatedBlogs(related);
      } catch {
        setRelatedBlogs([]);
      }
    };

    fetchRelated();
  }, [blog]);

  /* ============================================================
     HELPERS
  ============================================================ */

  const formatDate = (d) => {
    if (!d) return "N/A";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  /* Fallback reading time when the backend value is missing */
  const readTime =
    blog?.readTime ||
    Math.max(
      1,
      Math.round(
        String(blog?.content || "")
          .replace(/<[^>]*>/g, " ")
          .split(/\s+/)
          .filter(Boolean).length / 200
      )
    );

  /* ============================================================
     LOADING STATE
  ============================================================ */

  if (loading) {
    return (
      <>
        
        <div className="single-blog-page">
          <div className="single-blog-loading">
            <div className="loading-spinner"></div>
            <p>Loading article...</p>
          </div>
        </div>

        
      </>
    );
  }

  /* ============================================================
     ERROR / NOT FOUND STATE
  ============================================================ */

  if (error || !blog) {
    return (
      <>
      

        <div className="single-blog-page">
          <div className="single-blog-not-found">
            <div className="not-found-icon">📭</div>

            <h1>
              {error === "Blog not found"
                ? "Blog Not Found"
                : "Something went wrong"}
            </h1>

            <p>
              {error === "Blog not found"
                ? `The blog post with slug "${slug}" does not exist. It may have been removed or the link is incorrect.`
                : error}
            </p>

            <div className="not-found-actions">
              <button
                onClick={() => navigate("/blog")}
                className="btn-back-blog"
              >
                ← Back to All Blogs
              </button>

              <button onClick={() => navigate("/")} className="btn-home">
                🏠 Go to Homepage
              </button>
            </div>
          </div>
        </div>

        
      </>
    );
  }

  /* ============================================================
     RENDER BLOG CONTENT
  ============================================================ */

  return (
    <>
     

      <div className="single-blog-page">
        {/* ============ BREADCRUMB ============ */}
        <nav
          className="single-blog-breadcrumb"
          aria-label="Breadcrumb"
        >
          <Link to="/">Home</Link>
          <span className="crumb-sep">›</span>
          <Link to="/blog">Blog</Link>
          <span className="crumb-sep">›</span>
          <span className="current">
            {blog.title || "Blog Post"}
          </span>
        </nav>

        {/* ============ MAIN ARTICLE ============ */}
        <article className="single-blog-article">
          {/* Featured Image — SEO alt from Blog Manager */}
          {blog.featuredImage && (
            <div className="single-blog-hero-img">
              <img
                src={blog.featuredImage}
                alt={
                  blog.imageAlt ||
                  blog.title ||
                  "Sezhi Billing blog article"
                }
              />

              <div className="hero-overlay">
                <span className="category-badge">
                  {blog.category || "General"}
                </span>
              </div>
            </div>
          )}

          {/* Meta Info */}
          <div className="single-blog-meta">
            <div className="meta-left">
              <span className="meta-author">
                <span className="author-avatar">
                  {(blog.author || "S").charAt(0).toUpperCase()}
                </span>
                {blog.author || "Sezhi Team"}
              </span>

              <span className="meta-separator">•</span>

              <span className="meta-date">
                📅 {formatDate(blog.pubDate)}
              </span>

              <span className="meta-separator">•</span>

              <span className="meta-readtime">
                ⏱ {readTime} min read
              </span>

              <span className="meta-separator">•</span>

              <span
                className={`meta-status ${(
                  blog.status || ""
                ).toLowerCase()}`}
              >
                {blog.status || "Published"}
              </span>
            </div>
          </div>

          {/* Title */}
          <h1 className="single-blog-title">
            {blog.title || "Untitled Blog"}
          </h1>

          {/* Excerpt */}
          {blog.excerpt && (
            <p className="single-blog-excerpt">{blog.excerpt}</p>
          )}

          {/* Tags */}
          {(blog.tags || []).length > 0 && (
            <div className="single-blog-tags">
              {(blog.tags || []).map((tag) => (
                <span key={tag} className="tag-pill">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Divider */}
          <div className="single-blog-divider"></div>

          {/* Content */}
          <div
            className="single-blog-content"
            dangerouslySetInnerHTML={{
              __html: blog.content || "<p>No content available.</p>",
            }}
          />

          {/* Content Divider */}
          <div className="single-blog-divider"></div>

          {/* Tags Bottom */}
          {(blog.tags || []).length > 0 && (
            <div className="single-blog-tags-bottom">
              <span className="tags-label">Tags:</span>

              {(blog.tags || []).map((tag) => (
                <span key={tag} className="tag-pill-bottom">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Share Section */}
          <div className="single-blog-share">
            <span>Share this article:</span>

            <div className="share-buttons">
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  blog.title || ""
                )}&url=${encodeURIComponent(
                  `https://SezhiBilling.com/blog/${blog.slug}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn twitter"
              >
                𝕏 Share
              </a>

              <a
                href={`https://wa.me/?text=${encodeURIComponent(
                  blog.title +
                    " " +
                    `https://SezhiBilling.com/blog/${blog.slug}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn whatsapp"
              >
                WhatsApp
              </a>

              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                  `https://SezhiBilling.com/blog/${blog.slug}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn linkedin"
              >
                LinkedIn
              </a>
            </div>
          </div>

          {/* Back to Blog */}
          <div className="single-blog-back">
            <Link to="/blog" className="btn-back-all">
              ← Back to All Blogs
            </Link>
          </div>
        </article>

        {/* ============ RELATED BLOGS ============ */}
        {relatedBlogs.length > 0 && (
          <section className="single-blog-related">
            <h2>Related Articles</h2>

            <div className="related-grid">
              {relatedBlogs.map((related) => (
                <article
                  key={related._id || related.slug}
                  className="related-card"
                >
                  {related.featuredImage && (
                    <div className="related-img">
                      <img
                        src={related.featuredImage}
                        alt={
                          related.imageAlt ||
                          related.title ||
                          "Related article"
                        }
                        loading="lazy"
                      />
                    </div>
                  )}

                  <div className="related-body">
                    <span className="related-category">
                      {related.category}
                    </span>

                    <h3>{related.title}</h3>

                    <p>
                      {(related.excerpt || "").substring(0, 100)}...
                    </p>

                    <button
                      onClick={() => navigate(`/blog/${related.slug}`)}
                      className="related-readmore"
                    >
                      Read More →
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>

      
    </>
  );
}